import { ReactElement, useEffect, useMemo, useState } from 'react';
import { HiOutlineCheckCircle } from 'react-icons/hi2';
import { toast } from 'react-hot-toast';
import Button from '../../../components/Button';
import AccordionIcon from './SessionConfigs/AccordionIcon';
import { Session } from '../../../types/Session';
import { useUpdateSession, useInvalidateSession } from '../../../hooks/useSessions';

interface SessionHealthVarsFormProps {
  session: Session;
  sessionId: string;
}

// Mirrors the server WARNING_CONSTS defaults (common/cscore/defaults/
// default_consts.py). Shown as placeholders; an empty field inherits the
// default. Only the thresholds wired into the HealthCheck escalation are
// exposed — see session_health_vars_type.py.
// Each WARNING_CONSTS field is a [warn, error] band; these two are single-stage
// (wheel-skip is error-only, dropped-packets is warn-only), so the form edits
// the one meaningful stage and writes null for the other. `stage` says which
// tuple slot the scalar input maps to.
type Band = [number | null, number | null];
const THRESHOLD_FIELDS: ReadonlyArray<{
  key: string; label: string; def: number; stage: 'warn' | 'error'; hint: string;
}> = [
  {
    key: 'speed_wheel_skip_ratio_max',
    label: 'Wheel-skip error ratio',
    def: 0.01,
    stage: 'error',
    hint: 'Run errors when the wheel-skip ratio exceeds this fraction (default 0.01 = 1%). Raise to tolerate more skips.',
  },
  {
    key: 'ant_dropped_packets_frac_max',
    label: 'ANT dropped-packet tolerance',
    def: 0.15,
    stage: 'warn',
    hint: 'Run warns when a required ANT sensor drops more than this fraction of packets (default 0.15 = 15%).',
  },
];

// Build the {key: 'stringValue'} editing map from the session's stored overrides,
// reading the value from each field's meaningful tuple stage.
function toFormValues(session: Session): Record<string, string> {
  const thresholds = session.session_health_vars?.thresholds ?? {};
  const result: Record<string, string> = {};
  for (const field of THRESHOLD_FIELDS) {
    const band = (thresholds as Record<string, Band | null | undefined>)[field.key];
    const val = band ? band[field.stage === 'warn' ? 0 : 1] : undefined;
    result[field.key] = val != null ? String(val) : '';
  }
  return result;
}

export default function SessionHealthVarsForm({ session, sessionId }: SessionHealthVarsFormProps): ReactElement {
  const [show, setShow] = useState(false);
  const initial = useMemo(() => toFormValues(session), [session]);
  const [values, setValues] = useState<Record<string, string>>(initial);

  // Re-sync after a save (the session prop refetches with the new overrides).
  useEffect(() => {
    setValues(toFormValues(session));
  }, [session]);

  const updateSession = useUpdateSession(sessionId);
  const invalidateSession = useInvalidateSession(sessionId);

  const dirty = THRESHOLD_FIELDS.some((f) => (values[f.key] ?? '') !== (initial[f.key] ?? ''));

  const handleSave = async (): Promise<void> => {
    const thresholds: Record<string, Band> = {};
    for (const field of THRESHOLD_FIELDS) {
      const raw = (values[field.key] ?? '').trim();
      if (raw === '') continue;
      const num = Number(raw);
      if (Number.isNaN(num)) {
        toast.error(`${field.label} must be a number`);
        return;
      }
      // Write the scalar into its meaningful stage; null inherits the other.
      thresholds[field.key] = field.stage === 'warn' ? [num, null] : [null, num];
    }
    // No overrides left → clear the column so the run inherits all defaults.
    const payload = Object.keys(thresholds).length > 0 ? { version: 1, thresholds } : null;
    try {
      await updateSession.mutateAsync({ session_health_vars: payload });
      await invalidateSession();
      toast.success('Health thresholds saved — reprocess affected runs to apply');
    } catch (err) {
      toast.error('Failed to save health thresholds');
    }
  };

  return (
    <div className="cr-mt-6">
      <div
        className="cr-justify-left cr-flex cr-cursor-pointer cr-items-center cr-gap-2"
        onClick={() => setShow(!show)}
      >
        <AccordionIcon show={show} />
        <h4 className="cr-text-md cr-font-medium cr-text-blue-900">Health Thresholds</h4>
      </div>
      {show && (
        <div className="cr-pt-4">
          <div className="cr-mb-4 cr-flex cr-flex-col cr-gap-4 cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-md">
            <p className="cr-text-sm cr-text-gray-600">
              Relax a data-quality warning limit for this session. A warning relaxed past its default still
              appears on the run (under &ldquo;Snoozed&rdquo;) but no longer flags the run. Leave a field blank
              to inherit the default.
            </p>
            {THRESHOLD_FIELDS.map((field) => (
              <div key={field.key} className="cr-flex cr-flex-col cr-gap-1">
                <label htmlFor={`shv-${field.key}`} className="cr-text-sm cr-font-medium cr-text-gray-700">
                  {field.label}
                </label>
                <div className="cr-flex cr-items-center cr-gap-3">
                  <input
                    id={`shv-${field.key}`}
                    type="number"
                    step="any"
                    min="0"
                    placeholder={`default ${field.def}`}
                    className="cr-h-11 cr-w-32 cr-rounded cr-border cr-border-gray-300 cr-bg-white cr-px-4 cr-text-base focus:cr-border-blue-500 focus:cr-outline-none"
                    value={values[field.key] ?? ''}
                    onChange={(e) => setValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                  />
                  <span className="cr-text-xs cr-text-gray-600">{field.hint}</span>
                </div>
              </div>
            ))}
            {dirty && (
              <Button
                onClick={handleSave}
                disabled={updateSession.isPending}
                icon={<HiOutlineCheckCircle className="cr-h-5 cr-w-5 cr-stroke-[2.2px]" />}
                className="cr-h-11 !cr-text-base !cr-font-semibold !cr-normal-case"
              >
                {updateSession.isPending ? 'Saving...' : 'Save'}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
